import { gsap, ScrollTrigger, SplitText, prefersReducedMotion } from './gsap'

const START = 'top 88%'

const isOnScreen = (el) => {
  const r = el.getBoundingClientRect()
  return r.bottom > 0 && r.top < window.innerHeight
}

/**
 * Reveals declarativos dentro de `scope`. Llamar dentro de un gsap.context
 * (ver mixins/motion.js) para que todo se revierta al desmontar.
 *
 *   data-reveal            fade + subida, en lotes
 *   data-reveal="lines"    líneas enmascaradas
 *   data-reveal="chars"    letras enmascaradas
 *   data-reveal="image"    cortina de abajo hacia arriba + zoom interno
 *   data-reveal="line"     línea de 1px que se dibuja
 *   data-parallax="0.2"    parallax con scrub
 *
 * Los elementos ya animados se marcan para poder volver a llamar
 * a scrollEffects cuando llegan datos nuevos de la API.
 */
export function scrollEffects(scope) {
  if (!scope || prefersReducedMotion()) return

  const fresh = (selector) =>
    Array.from(scope.querySelectorAll(selector)).filter((el) => {
      if (el.dataset.revealed) return false
      el.dataset.revealed = '1'
      return true
    })

  const ups = fresh('[data-reveal=""], [data-reveal="up"]')
  if (ups.length) {
    gsap.set(ups, { y: 60, opacity: 0 })
    ScrollTrigger.batch(ups, {
      start: START,
      once: true,
      onEnter: (batch) => {
        const visible = batch.filter(isOnScreen)
        const passed = batch.filter((el) => !visible.includes(el))
        if (passed.length) gsap.set(passed, { y: 0, opacity: 1, overwrite: true })
        if (visible.length) {
          gsap.to(visible, {
            y: 0,
            opacity: 1,
            duration: 1.1,
            stagger: Math.min(0.08, 0.5 / visible.length),
            ease: 'expo.out',
            overwrite: true,
            clearProps: 'transform'
          })
        }
      }
    })
  }

  fresh('[data-reveal="lines"]').forEach((el) => {
    SplitText.create(el, {
      type: 'lines',
      mask: 'lines',
      autoSplit: true,
      onSplit: (self) =>
        gsap.from(self.lines, {
          yPercent: 110,
          duration: 1.2,
          stagger: 0.08,
          ease: 'expo.out',
          scrollTrigger: { trigger: el, start: START, once: true }
        })
    })
  })

  fresh('[data-reveal="chars"]').forEach((el) => {
    SplitText.create(el, {
      type: 'words,chars',
      mask: 'chars',
      autoSplit: true,
      onSplit: (self) =>
        gsap.from(self.chars, {
          yPercent: 110,
          duration: 1.2,
          stagger: 0.025,
          ease: 'expo.out',
          scrollTrigger: { trigger: el, start: 'top 90%', once: true }
        })
    })
  })

  fresh('[data-reveal="image"]').forEach((el) => {
    const inner = el.querySelector('img, video')
    const tl = gsap.timeline({ scrollTrigger: { trigger: el, start: START, once: true } })
    tl.fromTo(
      el,
      { clipPath: 'inset(100% 0% 0% 0%)' },
      { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.4, ease: 'expo.inOut' }
    )
    if (inner) {
      tl.from(inner, { scale: 1.3, duration: 1.8, ease: 'expo.out' }, 0.2)
      tl.set(inner, { clearProps: 'transform' })
    }
    tl.set(el, { clearProps: 'clipPath' })
  })

  fresh('[data-reveal="line"]').forEach((el) => {
    gsap.from(el, {
      scaleX: 0,
      transformOrigin: 'left center',
      duration: 1.6,
      ease: 'expo.inOut',
      scrollTrigger: { trigger: el, start: 'top 92%', once: true }
    })
  })

  fresh('[data-parallax]').forEach((el) => {
    const strength = Number(el.dataset.parallax) || 0.2
    gsap.fromTo(
      el,
      { yPercent: -strength * 50 },
      {
        yPercent: strength * 50,
        ease: 'none',
        scrollTrigger: {
          trigger: el.parentElement || el,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true
        }
      }
    )
  })
}
